import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  UserCheck,
  Ban,
  RefreshCw,
  Search,
  Trash2,
  CircleSlash,
  CheckCircle2,
  KeyRound,
  AlertTriangle,
} from "lucide-react";

const TONE = {
  active: "bg-[#0F5E48]/10 text-[#0F5E48] border-[#0F5E48]/20",
  suspended: "bg-red-500/10 text-red-700 border-red-500/30",
};

function initials(name = "", email = "") {
  const src = (name || email || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

export default function AdminCustomersPage() {
  const [data, setData] = useState({ items: [], stats: { total: 0, active: 0, suspended: 0 } });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  const load = (q = "") => {
    setLoading(true);
    api
      .get("/admin/user-accounts", { params: q ? { q } : {} })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load user accounts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => load(query), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const toggleSuspend = async (row) => {
    const path = row.status === "active" ? "suspend" : "unsuspend";
    try {
      await api.post(`/admin/user-accounts/${row.id}/${path}`);
      toast.success(row.status === "active" ? "Account suspended" : "Account reactivated");
      load(query);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Permanently delete ${row.email}? This unlinks their submissions but keeps the history.`)) return;
    try {
      await api.delete(`/admin/user-accounts/${row.id}`);
      toast.success("Account deleted");
      load(query);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const setPassword = async (row) => {
    const newPwd = window.prompt(
      `Set a new password for ${row.email}.\n\nGive this password to the customer via chat/WhatsApp and ask them to change it after signing in.\nMinimum 8 characters.`,
      "",
    );
    if (newPwd === null) return;
    if (!newPwd || newPwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    try {
      await api.post(`/admin/user-accounts/${row.id}/set-password`, {
        new_password: newPwd,
      });
      toast.success(`Password updated for ${row.email}. Send it to them now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set password");
    }
  };

  const items = data.items || [];

  const purgeAll = async () => {
    if (purgeConfirm.trim() !== "PURGE ALL CUSTOMERS") {
      toast.error("Type PURGE ALL CUSTOMERS exactly to confirm");
      return;
    }
    setPurging(true);
    try {
      const r = await api.post("/admin/user-accounts/purge-all", { confirm: purgeConfirm.trim() });
      toast.success(`Deleted ${r.data.deleted} accounts. Ready for fresh signups.`);
      setPurgeOpen(false);
      setPurgeConfirm("");
      load("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-customers">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0F5E48]">
            User accounts
          </span>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0A1628] mt-1.5">
            Registered customers
          </h1>
        </div>
      </div>

      {/* Stats hero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Stat label="Total users" value={data.stats?.total ?? 0} Icon={Users} tone="ink" testid="users-stat-total" />
        <Stat label="Active" value={data.stats?.active ?? 0} Icon={UserCheck} tone="emerald" testid="users-stat-active" />
        <Stat label="Suspended" value={data.stats?.suspended ?? 0} Icon={Ban} tone="red" testid="users-stat-suspended" />
      </div>

      {/* Search + refresh */}
      <div className="bg-white border border-[#E1D8C6] rounded-2xl soft-shadow p-3 flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#777E89]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users by name or email…"
            data-testid="users-search"
            className="w-full h-10 pl-9 pr-3 bg-transparent text-sm text-[#0F1419] outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => load(query)}
          data-testid="users-refresh"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#0F1419] text-white text-sm font-semibold hover:opacity-90"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E1D8C6] rounded-2xl overflow-hidden soft-shadow">
        <div className="hidden md:grid grid-cols-[1.8fr,2.2fr,0.8fr,0.9fr,1fr,1fr] gap-3 px-5 py-3 bg-[#FBF8F2] text-[10px] uppercase tracking-[0.14em] text-[#777E89] font-semibold border-b border-[#E1D8C6]">
          <span>User</span>
          <span>Email</span>
          <span className="text-right">Submissions</span>
          <span>Status</span>
          <span>Joined</span>
          <span className="text-right">Actions</span>
        </div>

        {items.length === 0 && !loading && (
          <div className="px-5 py-12 text-center text-sm text-[#777E89]">
            No registered accounts yet.
          </div>
        )}

        <ul className="divide-y divide-[#E1D8C6]">
          {items.map((r) => (
            <li
              key={r.id}
              data-testid={`user-row-${r.email}`}
              className="md:grid md:grid-cols-[1.8fr,2.2fr,0.8fr,0.9fr,1fr,1fr] md:gap-3 px-5 py-3.5 md:items-center"
            >
              {/* User */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#0F1419] text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {initials(r.name, r.email)}
                </div>
                <span className="font-medium text-[#0A1628] truncate">{r.name || "—"}</span>
              </div>
              {/* Email */}
              <div className="text-sm text-[#4A535E] truncate md:pr-3 mt-1 md:mt-0 break-all">
                {r.email}
              </div>
              {/* Submissions */}
              <div className="text-sm font-mono-display text-[#0A1628] md:text-right mt-1 md:mt-0">
                <span className="md:hidden text-[10px] uppercase tracking-wider text-[#777E89] mr-1">Subs:</span>
                {r.submissions}
              </div>
              {/* Status */}
              <div className="mt-1 md:mt-0">
                <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-[11px] font-semibold border ${TONE[r.status]}`}>
                  {r.status === "active" ? <CheckCircle2 className="w-3 h-3" /> : <CircleSlash className="w-3 h-3" />}
                  {r.status === "active" ? "Active" : "Suspended"}
                </span>
              </div>
              {/* Joined */}
              <div className="text-xs text-[#777E89] font-mono-display mt-1 md:mt-0">
                {fmtDate(r.joined)}
              </div>
              {/* Actions */}
              <div className="flex items-center md:justify-end gap-2 mt-2 md:mt-0">
                <button
                  type="button"
                  onClick={() => setPassword(r)}
                  data-testid={`user-set-password-${r.email}`}
                  title="Reset this customer's password (give them the new password directly)"
                  className="h-8 w-8 rounded-md border border-[#0F5E48]/30 text-[#0F5E48] hover:bg-[#0F5E48]/10 flex items-center justify-center transition-colors"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => toggleSuspend(r)}
                  data-testid={`user-suspend-${r.email}`}
                  title={r.status === "active" ? "Suspend account" : "Reactivate account"}
                  className={`h-8 w-8 rounded-md border flex items-center justify-center transition-colors ${
                    r.status === "active"
                      ? "border-amber-300 text-amber-600 hover:bg-amber-50"
                      : "border-[#0F5E48]/30 text-[#0F5E48] hover:bg-[#0F5E48]/10"
                  }`}
                >
                  {r.status === "active" ? <CircleSlash className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => remove(r)}
                  data-testid={`user-delete-${r.email}`}
                  title="Delete account"
                  className="h-8 w-8 rounded-md border border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Danger Zone — bulk purge all customer accounts (used before launch) */}
      <div className="mt-8 rounded-2xl border-2 border-dashed border-red-300 bg-red-50/40 p-5" data-testid="users-danger-zone">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-display text-base font-bold text-red-700">Danger Zone — Purge all user accounts</div>
            <div className="text-sm text-[#4a4d5f] mt-1 leading-relaxed">
              Deletes every row in the <code className="text-[12.5px]">customers</code> collection so new signups start fresh from #1.
              Historical gift-card submissions stay for audit but are unlinked from the deleted accounts.
              This action is irreversible.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPurgeOpen(true)}
            data-testid="users-purge-open"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 shrink-0"
          >
            <Trash2 className="w-4 h-4" /> Purge all user accounts
          </button>
        </div>
      </div>

      {purgeOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !purging && setPurgeOpen(false)}
          data-testid="users-purge-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-lg font-bold text-[#0A1628]">Confirm purge</div>
                <div className="text-xs text-[#777E89]">This cannot be undone.</div>
              </div>
            </div>
            <p className="text-sm text-[#4a4d5f] mb-4 leading-relaxed">
              You are about to permanently delete <strong>{data.stats?.total ?? 0}</strong> customer account{(data.stats?.total ?? 0) === 1 ? "" : "s"}.
              To confirm, type <code className="text-[13px] bg-[#f2f4fb] px-1.5 py-0.5 rounded">PURGE ALL CUSTOMERS</code> below.
            </p>
            <input
              type="text"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder="Type PURGE ALL CUSTOMERS"
              data-testid="users-purge-input"
              className="w-full h-11 px-3 rounded-lg border border-[#e1d8c6] text-sm text-[#0F1419] outline-none focus:border-red-500 mb-4"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setPurgeOpen(false); setPurgeConfirm(""); }}
                disabled={purging}
                data-testid="users-purge-cancel"
                className="h-10 px-4 rounded-full bg-[#f2f4fb] text-[#0F1419] text-sm font-semibold hover:bg-[#e6e8f2] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={purgeAll}
                disabled={purging || purgeConfirm.trim() !== "PURGE ALL CUSTOMERS"}
                data-testid="users-purge-confirm"
                className="h-10 px-4 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {purging ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Purging…</> : <><Trash2 className="w-3.5 h-3.5" /> Purge everything</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, Icon, tone, testid }) {
  const toneRing = {
    emerald: "bg-[#0F5E48]/10 text-[#0F5E48]",
    red: "bg-red-500/10 text-red-600",
    ink: "bg-[#0F1419]/8 text-[#0F1419]",
  }[tone];
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#E1D8C6] bg-white p-4 soft-shadow flex items-center gap-3.5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneRing}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="font-display text-2xl font-bold text-[#0F1419] leading-none">{value}</div>
        <div className="text-[11px] text-[#777E89] mt-1 uppercase tracking-wider font-semibold">{label}</div>
      </div>
    </div>
  );
}
